import { ModalScreen } from "./modal-screen";
import { customElement, property, state } from "lit/decorators.js";
import { html } from "lit";
import { loadResource } from "@/utils/resource";
import { extractFrameFromSpriteSheet } from "@/utils/spritesheet";
import { AsepriteResource } from "@excaliburjs/plugin-aseprite";
import AsyncEventDispatcher from "@/utils/event-dispatcher";
import { CustomToast } from "./toast";
("./atoms/label");

@customElement("shop-screen")
export class Shop extends ModalScreen {
  @property({ type: Array }) tabs: { slug: string; label: string }[] = [];
  @property({ type: Array }) items: {
    id: number;
    description: string;
    category: string;
    price: number;
    image: string;
  }[] = [];
  @property({ type: String }) activeTab: string = "";

  @state() currentPage: number = 1;
  @state() itemsPerPage: number = 12;
  totalItems: number = 0;
  @property() furnitures: { slug: string; img?: string; amount: number }[] = [];
  private furnitureAssets: Map<string, string> = new Map();

  constructor() {
    super();
  }

  async connectedCallback() {
    super.connectedCallback();
  }

  private async loadAsset(slug: string) {
    if (this.furnitureAssets.has(slug)) {
      return this.furnitureAssets.get(slug);
    }

    try {
      const resource = await loadResource(slug);
      const dataUrl = this.extractDataUrl(resource);
      this.furnitureAssets.set(slug, dataUrl);
      return dataUrl;
    } catch (error) {
      console.error(`Error loading asset: ${slug}`, error);
      return "/images/fallback.png"; // Provide a default fallback asset
    }
  }

  private extractDataUrl(resource: AsepriteResource) {
    const sprite = resource.data.getSpriteSheet().sprites[0];

    if (sprite && sprite.image?.data && sprite.sourceView) {
      const canvas = extractFrameFromSpriteSheet(
        sprite.image.data,
        sprite.sourceView
      );
      return canvas.toDataURL();
    }

    console.error("Invalid sprite data or source view.");
    return "/images/fallback.png";
  }

  public async openModal(): Promise<void> {
    this.isOpen = true;

    this.tabs = [
      { slug: "furnitures", label: "Furnitures" },
      // { slug: "clothing", label: "Clothing" },
      // { slug: "pets", label: "Pets" },
    ];

    this.selectTab("furnitures");
  }

  async fetchItems(page = 1, limit = 4, category = "") {
    try {
      const categoryQuery = category ? `&category=${category}` : "";
      const response = await fetch(
        `${process.env.BASE_API_URL}/shop?page=${page}&limit=${limit}${categoryQuery}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Update items and pagination state
        this.items = data.items;
        this.totalItems = data.totalItems;
        this.currentPage = data.currentPage;
        this.itemsPerPage = limit;

        // Load assets in parallel
        await Promise.all(
          this.items.map(async (item: any) => {
            item.image = await this.loadAsset(item.slug);
          })
        );

        this.requestUpdate();
      } else {
        console.error("Failed to fetch shop items:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching shop items:", error);
    }
  }

  selectTab(tabId: string) {
    this.activeTab = tabId;
    this.currentPage = 1; // Reset to first page when switching tabs
    this.fetchItems(1, this.itemsPerPage, tabId);
  }

  changePage(direction: number) {
    const maxPage = Math.ceil(this.totalItems / this.itemsPerPage);
    const newPage = this.currentPage + direction;
    if (newPage > 0 && newPage <= maxPage) {
      this.currentPage = newPage;
      this.fetchItems(this.currentPage, this.itemsPerPage, this.activeTab);
    }
  }

  async buyItem(itemId: number, price: number) {
    const toast = document.querySelector("custom-toast") as CustomToast;

    try {
      const response = await fetch(`${process.env.BASE_API_URL}/shop-buy`, {
        method: "POST",
        body: JSON.stringify({ itemId }),
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();

        if (data.type === "success") {
          const dispatcher = AsyncEventDispatcher.getInstance();
          dispatcher.dispatchEvent("coin-update", {
            detail: { coins: price, action: "decrease" },
          });
        }

        toast?.openToast(data.message, "top-right", 3000, data.type);
      }
    } catch (error) {
      toast?.openToast(error, "top-right", 3000, "error");
    }
  }

  get filteredItems() {
    return this.items.filter(
      (item) => !this.activeTab || item.category === this.activeTab
    );
  }

  render() {
    if (!this.isOpen) return html``;

    return html`
      <div class="modal">
        <div class="modal__top">
          ${this.tabs.map(
            (tab) => html`
              <span
                class="modal__tab ${this.activeTab === tab.slug
                  ? "is-active"
                  : ""}"
                @click="${() => this.selectTab(tab.slug)}"
              >
                ${tab.label}
              </span>
            `
          )}
          <button class="modal__close" @click="${this.closeModal}">
            <span>Close</span>
          </button>
        </div>

        <div class="modal__body">
          <div class="cards">
            ${this.items.map(
              (item) => html`
                <div class="card">
                  <h3>${item.description}</h3>
                  <div class="card-image">
                    <img src=${item.image} alt=${item.description} />
                  </div>

                  ${item.price > 0
                    ? html`<button
                        class="btn"
                        @click="${() => this.buyItem(item.id, item.price)}"
                      >
                        <span class="btn__amount">${item.price}</span>Buy
                      </button>`
                    : ""}
                </div>
              `
            )}
          </div>
          ${this.items.length > 0
            ? html`
                <div class="pagination">
                  <button
                    class="btn is-prev ${this.currentPage === 1
                      ? "is-disabled"
                      : ""}"
                    ?disabled="${this.currentPage === 1}"
                    @click="${() => this.changePage(-1)}"
                  >
                    &#8249;
                  </button>
                  <span class="label">Page ${this.currentPage}</span>
                  <button
                    class="btn is-next ${this.currentPage ===
                    Math.ceil(this.totalItems / this.itemsPerPage)
                      ? "is-disabled"
                      : ""}"
                    ?disabled="${this.currentPage ===
                    Math.ceil(this.totalItems / this.itemsPerPage)}"
                    @click="${() => this.changePage(1)}"
                  >
                    &#8250;
                  </button>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }
}
